import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, Row } from 'ant-design-vue';
import lodash from 'lodash';
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { addModal, setIsLoading as setIsLoadingApp } from '../../../../app.logic';
import { createFieldSelect } from "../../../../components/input/template-builder/template-builder.logic";
import { setAppPageHeader } from "../../../../components/layout/app-layout/app-layout.logic";
import { createColumnSelect } from "../../../../components/table/c-table.logic";
import i18n from "../../../../i18n";
import store from "../../../../store";
import { createButtonActive, createButtonArchive, createButtonCreate, createButtonDelete, createButtonEdit, createButtonSave, createButtonView } from "../../../../utils/button";
import { modalStyleIFrame } from "../../../../utils/constant";
import { checkAttribute, initUseForm, onClickActive, onClickActiveAll, onClickArchive, onClickArchiveAll, onClickCreate, onClickDelete, onClickDeleteAll, onClickEdit, onClickView } from '../../../../utils/form';
import { createLoading } from '../../../../utils/loading';
import { has } from "../../../../utils/menu-context";
import { createAppPageHeaderTop, createManageActive, createManageArchive, createManageDelete, createManageExportCsv, createManageExtraCreate, createManageSelect, createPage, createRowActionEdit, createRowActionView } from '../../../../utils/page';
import { required, validateError } from "../../../../utils/validate";

const CIFrame = defineAsyncComponent(() => import('../../../../components/iframe/CIFrame'))

export const { isLoading, setIsLoading } = createLoading()

export const actionPrefix = 'resource/v1/master/departmentResponsible'
export const routerPrefix = 'department-responsibles'

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form })
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria })
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id })
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id })
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } })
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids })
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids })
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } })
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria })
export const callDepartment = ({ searchQuery }) => store.dispatch('resource/v1/master/department/cacheFindAll', { searchQuery, where: [{ name: "refParent.id", type: "UUID", operator: '=', value: null }, { name: "refDepartmentType.key", type: "String", operator: 'in', value: ['FIQC', 'FIRC'] }] })
export const callProvince = ({ searchQuery }) => store.dispatch('resource/v1/master/province/cacheFindAll', { name: searchQuery })

export const onSearch = ({ page, isSet = true }) => {
    setIsLoading(true)
    window.onSearch?.({ page })
    return callSearch({ criteria: { ...page, data: [] } })
        .then(page => (isSet ? (state.page = { ...page }) : page))
        .finally(() => setIsLoading(false))
}

export const onClickSave = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true)
            return callSave({ form: data.useForm.modelRef })
                .finally(() => setIsLoadingApp(false))
                .then((record) => onClickView({ record, routerPrefix }))
        })
        .catch((error) => validateError({ error }));
}

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch((error) => {
            validateError({ error })
            throw error
        });
}

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ['**'],
            page: 0,
            size: 1,
            where: [{ name: 'id', type: 'UUID', operator: '=', value: id }],
        }
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: 'id', type: 'UUID', operator: '=', value: id }],
            children: [],
        }
    }
}

export const createColumns = () => {
    const columns = [
        createColumnSelect({
            title: () => i18n.global.t("views.departmentResponsible.refDepartment"),
            dataIndex: "refDepartment.nameTh",
            customRender: ({ record }) => <div>{record.refDepartment?.nameTh}</div>,
            inputProp: () => ({ list: callDepartment, optLabel: 'nameTh' }),
            search: { name: "refDepartment.id" }
        }),
        createColumnSelect({
            title: () => i18n.global.t("views.departmentResponsible.refProvince"),
            dataIndex: "refProvince.name",
            customRender: ({ record }) => <div>{record.refProvince?.name}</div>,
            inputProp: () => ({ list: callProvince, optLabel: 'name' }),
            align: 'center',
            search: { name: "refProvince.id" }
        }),
    ]
    return columns
}

export const createForm = () => {
    return {
        refDepartment: null,
        refProvince: null,
    }
}

export const createRules = () => {
    return {
        refDepartment: [required],
        refProvince: [required],
    }
}

export const createField = ({ props, data }) => {
    const viewOnly = props.viewOnly
    const useForm = data.useForm
    const modelRef = useForm.modelRef
    return [
        createFieldSelect({
            key: `refDepartment`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('views.departmentResponsible.refDepartment'),
            inputProp: () => ({
                value: modelRef.refDepartment,
                'onUpdate:value': value => modelRef.refDepartment = value,
                disabled: viewOnly,
                list: callDepartment,
                optLabel: "nameTh",
            }),
            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.nameTh`) }
        }),
        createFieldSelect({
            key: `refProvince`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('views.departmentResponsible.refProvince'),
            inputProp: () => ({
                value: modelRef.refProvince,
                'onUpdate:value': value => modelRef.refProvince = value,
                disabled: viewOnly,
                list: callProvince,
                optLabel: "name",
            }),
            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.name`) }
        }),
    ]
}

export const createFormTitle = ({ data }) => {
    return data.useForm.modelRef.id ? `${data.useForm.modelRef.refDepartment?.nameTh} ${data.useForm.modelRef.refProvince?.name}` : i18n.global.t('button.create')
}

export const loadForm = ({ props, data }) => {
    setIsLoading(true)
    const { form } = props
    const { id, useForm } = data
    const { modelRef } = useForm
    let promise = null
    if (form) {
        promise = Promise.resolve({ data: [form] })
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ id }) })
    } else {
        promise = Promise.resolve({ data: [modelRef] })
    }
    return promise
        .then(page => initUseForm({ data, page, routerPrefix }))
        .finally(() => setIsLoading(false))
}

export const initState = ({ props }) => {
    const menuContext = props.menuContext
    state.manages = [
        createManageSelect({
            menuContext,
            getCheckboxProps: ({ record }) => ({ disabled: record.isActive !== true || window.selectGetCheckboxPropDisabled?.({ record }) })
        }),
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) => onClickActiveAll({ rowSelection, callActiveAll, state, onSearch, setIsLoading }),
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) => onClickArchiveAll({ rowSelection, callArchiveAll, state, onSearch, setIsLoading }),
        }),
        createManageDelete({
            menuContext,
            onClick: ({ rowSelection }) => onClickDeleteAll({ rowSelection, callDeleteAll, state, onSearch, setIsLoading }),
        }),
        createManageExportCsv({ menuContext, callData: ({ page }) => onSearch({ props, page: { ...lodash.cloneDeep({ ...page, page: 0, size: 1000000 }) }, isSet: false }) }),
    ]
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })]
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({ menuContext, onClick: ({ record }) => onClickEdit({ record, routerPrefix }) }),
    ]
}

export const initForm = ({ props, data, refs }) => {
    checkAttribute({ props, data, refs })
    data.form = createForm();
    data.rules = createRules();
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs });
    loadForm({ props, data, refs })
        .then(() => {
            if (props.setAppPageHeader) {
                setAppPageHeader({
                    slots: {
                        top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                        default: () => createAppPageHeaderDefault({ props, data, refs })
                    }
                });
            }
        })
}

export const createAppPageHeaderDefault = ({ props, data }) => {
    const col1 = []
    const col2 = []
    const { menuContext, viewOnly } = props
    const { id, isActive } = data.useForm.modelRef
    if (id && has({ menuContext, key: 'canCreate' })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: 'm-1' }))
    }
    if (!viewOnly && id && has({ menuContext, key: 'canView' })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: 'm-1' }))
    }
    if (viewOnly && id && isActive && has({ menuContext, key: 'canEdit' })) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: 'm-1' }))
    }
    if (!viewOnly && (!id || (id && isActive))) {
        col1.push(createButtonSave({ onClick: () => onClickSave({ data }), classStyle: 'm-1' }))
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: 'canActive' })) {
        col2.push(createButtonActive({ onClick: () => onClickActive({ record: { id }, callActive, setIsLoading: setIsLoadingApp, onClickEdit: () => onClickEdit({ record: { id }, routerPrefix }) }), classStyle: 'm-1' }))
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: 'canArchive' })) {
        col2.push(createButtonArchive({ onClick: () => onClickArchive({ record: { id }, callArchive, setIsLoading: setIsLoadingApp, onClickView: () => onClickView({ record: { id }, routerPrefix }) }), classStyle: 'm-1' }))
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: 'canDelete' })) {
        col2.push(createButtonDelete({ onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }), classStyle: 'm-1' }))
    }
    return <Row type="flex">
        <Col>{col1}</Col>
        <Col class="ml-auto">{col2}</Col>
    </Row>
}

export const openModalList = ({ bind, onIframeMounted }) => {
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleIFrame), bind),
        render: () => <CIFrame src={`${process.env.VUE_APP_PUBLIC_PATH}/app/master-mgt/department-responsibles?isLayout=false&canSelect=true&cacheQuery=true`} onIframeMounted={({ refs }) => onIframeMounted({ options, refs })}></CIFrame>
    })
    return options
}

export const state = reactive({
    columns: createColumns(),
    page: createPage({ options: { select: ['**'] } }),
    tableOptions: { search: { activeKey: 'Search' } }
})
